<template>
  <div v-if="carKnown" class="battery-ai-block" :class="{ 'battery-ai-block_embedded': embedded }">
    <slot v-if="embedded" name="trigger" :open="openModal" :car-label="carLabel">
      <button type="button" class="battery-ai-open-btn battery-ai-open-btn_embedded" @click="openModal">
        Помощь ИИ
      </button>
    </slot>
    <div v-else class="battery-ai-car-done">
      <div class="battery-ai-car-done-icon">✓</div>
      <div class="battery-ai-car-done-text">
        <span class="battery-ai-car-done-label">Подобрали машину</span>
        <span class="battery-ai-car-done-value">{{ carLabel }}</span>
      </div>
      <button type="button" class="battery-ai-open-btn" @click="openModal">Помощь ИИ</button>
    </div>

    <transition name="bai-fade">
      <div
        v-if="isModalOpen"
        v-body-portal
        key="battery-ai-modal"
        class="bai"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bai-title"
        @click.self="closeModal"
      >
        <div class="bai__app">
          <!-- Шапка -->
          <header class="bai__topbar">
            <div class="bai__brand">
              <div class="bai__logo">ИИ</div>
              <div>
                <h2 id="bai-title" class="bai__title">Помощь ИИ — подбор АКБ</h2>
                <p class="bai__subtitle">Сценарий для озвучивания клиенту в звонке</p>
              </div>
            </div>

            <div class="bai__vehicle" :title="carLabel">
              <svg class="bai__vehicle-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path fill="currentColor" d="M5 11l1.5-4.5h11L19 11v5h-2v-2H7v2H5v-5zm2.2-2h9.6L16 9H8l-.8 2zM7 16a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0zm7 0a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z"/>
              </svg>
              <span class="bai__vehicle-text">{{ carLabel }}</span>
            </div>

            <div class="bai__topbar-end">
              <div class="bai__settings">
                <div class="bai__setting-group">
                  <span class="bai__setting-label">Стиль</span>
                  <div class="bai__segments">
                    <button
                      v-for="s in styleOptions"
                      :key="s.value"
                      type="button"
                      class="bai__segment"
                      :class="{ 'bai__segment--on': botSettings.style === s.value }"
                      @click="setStyle(s.value)"
                    >{{ s.label }}</button>
                  </div>
                </div>
                <div class="bai__setting-group">
                  <span class="bai__setting-label">Ответ</span>
                  <div class="bai__segments">
                    <button
                      v-for="s in sizeOptions"
                      :key="s.value"
                      type="button"
                      class="bai__segment"
                      :class="{ 'bai__segment--on': botSettings.responseSize === s.value }"
                      @click="setResponseSize(s.value)"
                    >{{ s.label }}</button>
                  </div>
                </div>
              </div>
              <button type="button" class="bai__close" aria-label="Закрыть" @click="closeModal">×</button>
            </div>
          </header>

          <!-- Контент -->
          <div class="bai__content">
            <!-- Анкета -->
            <section class="bai__panel bai__panel--form">
              <div class="bai__panel-head">
                <span class="bai__step">1</span>
                <h3>Анкета клиента</h3>
              </div>

              <div class="bai__form">
                <div v-if="hasOrderTradeIn" class="bai__sync-box">
                  <div class="bai__sync-title">Из заказа (зачёт)</div>
                  <div class="bai__sync-row">
                    <span>Ёмкость</span>
                    <strong>{{ form.tradeInCapacityLabel || (form.tradeInCapacityAh + ' А·ч') }}</strong>
                  </div>
                  <div v-if="form.tradeInAmountRub != null" class="bai__sync-row">
                    <span>Скидка за зачёт</span>
                    <strong>{{ form.tradeInAmountRub }} руб.</strong>
                  </div>
                </div>

                <fieldset class="bai__fieldset">
                  <legend>Сдаст старый АКБ аналогичной ёмкости?</legend>
                  <div class="bai__chips">
                    <label
                      v-for="opt in tradeInOptions"
                      :key="opt.value"
                      class="bai__chip"
                      :class="{ 'bai__chip--on': form.tradeIn === opt.value }"
                    >
                      <input v-model="form.tradeIn" type="radio" :value="opt.value" />
                      {{ opt.label }}
                    </label>
                  </div>
                </fieldset>

                <div class="bai__field">
                  <label for="bai-city">Город клиента</label>
                  <input id="bai-city" v-model="form.city" type="text" class="bai__input" placeholder="Например, Минск" />
                  <p v-if="initialCity && form.city === initialCity" class="bai__field-hint">Подтянуто из адреса доставки заказа</p>
                </div>

                <fieldset class="bai__fieldset">
                  <legend>Тип доставки</legend>
                  <div v-if="form.deliveryTypeLabel" class="bai__delivery-badge">{{ form.deliveryTypeLabel }}</div>
                  <p v-else class="bai__field-hint">В заказе тип доставки не выбран</p>
                  <div class="bai__chips bai__chips--mt">
                    <button
                      v-for="opt in fulfillmentOptions"
                      :key="opt.value"
                      type="button"
                      class="bai__chip bai__chip--btn"
                      :class="{ 'bai__chip--on': form.fulfillment === opt.value }"
                      @click="onFulfillmentChange(opt.value)"
                    >{{ opt.label }}</button>
                  </div>
                </fieldset>

                <label class="bai__check">
                  <input v-model="form.agmEfb" type="checkbox" @change="scheduleCatalogReload" />
                  <span>Нужен AGM / EFB</span>
                </label>

                <fieldset class="bai__fieldset">
                  <legend>Диапазон цены, руб. <span class="bai__legend-sub">({{ priceRangeLabel }})</span></legend>
                  <div class="bai__price-range">
                    <div class="bai__price-field">
                      <label for="bai-price-min">От</label>
                      <input
                        id="bai-price-min"
                        v-model.number="form.priceMin"
                        type="number"
                        min="0"
                        step="1"
                        class="bai__input bai__input--num"
                      />
                    </div>
                    <div class="bai__price-field">
                      <label for="bai-price-max">До</label>
                      <input
                        id="bai-price-max"
                        v-model.number="form.priceMax"
                        type="number"
                        min="0"
                        step="1"
                        class="bai__input bai__input--num"
                      />
                    </div>
                  </div>
                  <p class="bai__field-hint">
                    Вилка {{ corpusPriceLabel }} — по всему корпусу. После ответа клиента укажите «до» и нажмите «Сформировать ответ 2».
                  </p>
                </fieldset>
              </div>

              <div v-if="error" class="bai__error">{{ error }}</div>
            </section>

            <!-- Сценарий для клиента -->
            <section class="bai__panel bai__panel--script" :class="{ 'bai__panel--script-ready': !!scriptText }">
              <div class="bai__panel-head">
                <span class="bai__step">2</span>
                <div>
                  <h3>Текст для клиента</h3>
                  <p class="bai__panel-desc">Читайте вслух — от первого лица, без правок на лету</p>
                </div>
              </div>

              <div class="bai__script-body">
                <div class="bai__script-upper">
                  <div v-if="loadingScript && !scriptText" class="bai__state bai__state--compact">
                    <div class="bai__loader" />
                    <p class="bai__state-title">Формируем сценарий…</p>
                    <p class="bai__state-sub">Сверяем посадку по drive2.ru · обычно 15–30 секунд</p>
                  </div>

                  <template v-if="!loadingScript || scriptText">
                    <div v-if="scriptStale" class="bai__script-stale">
                      Анкета изменилась — нажмите «Сформировать ответ» ещё раз, чтобы обновить текст.
                    </div>
                    <div v-if="scriptText" class="bai__script-phase">
                      {{ scriptPhase === 2 ? 'Ответ 2 — с учётом бюджета' : 'Ответ 1 — ходовые и вилка цен' }}
                    </div>
                    <div v-if="scriptText" ref="scriptBlock" class="bai__script-text">{{ scriptText }}</div>
                    <div v-if="drive2Check && drive2Check.summary" class="bai__drive2">
                      <div class="bai__drive2-title">Сверка drive2.ru</div>
                      <p class="bai__drive2-summary">{{ drive2Check.summary }}</p>
                      <p v-if="drive2CheckMeta" class="bai__drive2-meta">{{ drive2CheckMeta }}</p>
                      <div v-if="drive2Check.sources && drive2Check.sources.length" class="bai__drive2-links">
                        <a
                          v-for="(url, idx) in drive2Check.sources.slice(0, 3)"
                          :key="idx"
                          :href="url"
                          target="_blank"
                          rel="noopener noreferrer"
                        >источник {{ idx + 1 }}</a>
                      </div>
                    </div>
                    <p v-if="loadingScript && scriptText" class="bai__script-hint bai__script-hint--loading">
                      Обновляем сценарий…
                    </p>
                    <p v-if="!scriptText && !loadingScript" class="bai__script-hint">
                      Нажмите «Сформировать ответ» — озвучите вилку цен ({{ corpusPriceLabel }}).
                      После ответа клиента сузьте «до» слева и нажмите «Сформировать ответ 2».
                    </p>
                    <div
                      v-if="!scriptText && !loadingScript && !catalogLoading && !catalogItemsFiltered.length"
                      class="bai__state bai__state--compact"
                    >
                      <div class="bai__state-icon">🎙</div>
                      <p class="bai__state-title">Сценарий появится здесь</p>
                      <p class="bai__state-sub">Таблица АКБ ниже загрузится по марке автомобиля</p>
                    </div>
                  </template>
                </div>

                <div
                  v-if="displayMinskStores.length"
                  class="bai__minsk-stores"
                  :title="minskStoresTooltip"
                >
                  <span class="bai__minsk-label">Минск самовывоз:</span>
                  <span class="bai__minsk-line">{{ minskStoresCompactLine }}</span>
                </div>

                <div class="bai__script-catalog">
                  <div class="bai__catalog-title">
                    Подходящие АКБ
                    <span v-if="catalogItemsFiltered.length" class="bai__catalog-count">{{ catalogItemsFiltered.length }}</span>
                    <span v-if="priceRangeLabel !== 'не задан'" class="bai__catalog-filter">· {{ priceRangeLabel }}</span>
                  </div>
                  <battery-catalog-table
                    fill-height
                    :items="catalogItemsFiltered"
                    :loading="catalogLoading"
                    empty-text="Нет позиций по корпусу — проверьте тип корпуса в карточке ТС"
                  />
                </div>
              </div>
            </section>
          </div>

          <!-- Подвал -->
          <footer class="bai__footer">
            <span class="bai__footer-tip"><kbd>Esc</kbd> закрыть · текст пишется в поле «Помощь ИИ» заказа</span>
            <div class="bai__footer-btns">
              <button type="button" class="bai__btn bai__btn--ghost" @click="closeModal">Закрыть</button>
              <button
                type="button"
                class="bai__btn bai__btn--secondary"
                :disabled="loadingScript || !scriptReadyForPhase2"
                :title="scriptReadyForPhase2 ? '' : 'Сначала сформируйте ответ 1'"
                @click="requestScriptRefined"
              >
                <span v-if="loadingScript && scriptPhaseRequest === 2" class="bai__btn-loader bai__btn-loader--dark" />
                {{ (loadingScript && scriptPhaseRequest === 2) ? 'Формируем…' : 'Сформировать ответ 2' }}
              </button>
              <button
                type="button"
                class="bai__btn bai__btn--primary"
                :disabled="loadingScript"
                @click="requestScriptIntro"
              >
                <span v-if="loadingScript && scriptPhaseRequest === 1" class="bai__btn-loader" />
                {{ (loadingScript && scriptPhaseRequest === 1) ? 'Формируем…' : 'Сформировать ответ' }}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  applyOrderContextToForm,
  buildQuestionnairePayload,
  calcCatalogPriceBounds,
  mapDeliveryToFulfillment,
  normalizeCatalogItems,
  parsePriceNumber,
} from '@/constants/batteryAiOrderSync';
import BatteryCatalogTable from '@/components/BatteryCatalogTable.vue';
import { MINSK_PICKUP_STORES, isMinskCity } from '@/constants/minskPickupStores';

const SETTINGS_KEY = 'manager_battery_ai_bot_settings';
const API_URL = 'https://manager.1akb.by/manager-battery-ai-pick';
const CATALOG_URL = 'https://manager.1akb.by/manager-battery-ai-catalog';

const bodyPortal = {
  inserted(el) {
    el.__batteryAiPortaled = true;
    document.body.appendChild(el);
  },
  componentUpdated(el) {
    if (el.__batteryAiPortaled && el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
  },
  unbind(el) {
    if (el.parentNode) el.parentNode.removeChild(el);
    delete el.__batteryAiPortaled;
  },
};

export default {
  name: 'BatteryAiAssistant',
  components: { BatteryCatalogTable },
  directives: { bodyPortal },
  props: {
    vehicle: { type: Object, default: () => ({}) },
    initialCity: { type: String, default: '' },
    orderContext: { type: Object, default: () => ({}) },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      isModalOpen: false,
      loadingScript: false,
      scriptPhaseRequest: 0,
      catalogLoading: false,
      error: '',
      scriptText: '',
      scriptPhase: 0,
      catalogItems: [],
      catalogFullBounds: { min: null, max: null },
      pickupStoresFromApi: [],
      drive2Check: null,
      lastScriptFormKey: '',
      bodyOverflowBackup: '',
      catalogReloadTimer: null,
      catalogBoundsApplied: false,
      applyingCatalogBounds: false,
      form: {
        tradeIn: 'unknown',
        city: '',
        fulfillment: 'unknown',
        deliveryTypeCode: '',
        deliveryTypeLabel: '',
        tradeInCapacityAh: null,
        tradeInCapacityLabel: '',
        tradeInAmountRub: null,
        agmEfb: false,
        priceMin: null,
        priceMax: null,
      },
      botSettings: { style: 'business', responseSize: 'medium' },
      styleOptions: [
        { value: 'business', label: 'Деловой' },
        { value: 'friendly', label: 'Дружелюбный' },
        { value: 'brief', label: 'Краткий' },
      ],
      sizeOptions: [
        { value: 'short', label: 'Короткий' },
        { value: 'medium', label: 'Средний' },
        { value: 'long', label: 'Подробный' },
      ],
      tradeInOptions: [
        { value: 'yes', label: 'Да' },
        { value: 'no', label: 'Нет' },
        { value: 'unknown', label: 'Не уточняли' },
      ],
      fulfillmentOptions: [
        { value: 'pickup', label: 'Самовывоз' },
        { value: 'delivery', label: 'Доставка' },
        { value: 'unknown', label: 'Не уточняли' },
      ],
    };
  },
  computed: {
    hasOrderTradeIn() {
      return this.form.tradeIn === 'yes' && (this.form.tradeInCapacityAh || this.form.tradeInAmountRub);
    },
    priceRangeLabel() {
      const min = this.form.priceMin;
      const max = this.form.priceMax;
      if (min != null && max != null) return `${min} – ${max} руб.`;
      if (max != null) return `до ${max} руб.`;
      if (min != null) return `от ${min} руб.`;
      return 'не задан';
    },
    corpusPriceLabel() {
      const { min, max } = this.catalogFullBounds;
      if (min != null && max != null) return `${min}–${max} руб.`;
      if (max != null) return `до ${max} руб.`;
      if (min != null) return `от ${min} руб.`;
      return 'по каталогу';
    },
    drive2CheckMeta() {
      const d = this.drive2Check;
      if (!d) return '';
      const bits = [];
      if (d.recommended_capacity_ah) bits.push(`ёмкость ~${d.recommended_capacity_ah} Ач`);
      if (d.recommended_case) bits.push(`корпус ${d.recommended_case}`);
      if (d.recommended_dims_mm) bits.push(`размеры ${d.recommended_dims_mm}`);
      return bits.join(' · ');
    },
    catalogItemsFiltered() {
      const min = this.form.priceMin;
      const max = this.form.priceMax;
      if (min == null && max == null) return this.catalogItems;
      return this.catalogItems.filter((it) => {
        const p = parsePriceNumber(it.price);
        if (p == null) return true;
        if (min != null && p < min) return false;
        if (max != null && p > max) return false;
        return true;
      });
    },
    scriptReadyForPhase2() {
      return this.scriptPhase >= 1 && !!this.scriptText;
    },
    displayMinskStores() {
      if (this.pickupStoresFromApi && this.pickupStoresFromApi.length) {
        return this.pickupStoresFromApi;
      }
      if (isMinskCity(this.form.city)) return MINSK_PICKUP_STORES;
      return [];
    },
    minskStoresCompactLine() {
      return this.displayMinskStores
        .map((s) => this.formatMinskShortAddress(s.address) || s.title)
        .join(' · ');
    },
    minskStoresTooltip() {
      return this.displayMinskStores
        .map((s) => {
          const hours = s.hours ? ` (${s.hours})` : '';
          return `${s.address || s.title}${hours}`;
        })
        .join('\n');
    },
    formScriptKey() {
      return JSON.stringify(this.buildQuestionnaire({ includeCorpusBounds: true }));
    },
    scriptStale() {
      return !!(
        this.scriptText
        && this.lastScriptFormKey
        && this.formScriptKey !== this.lastScriptFormKey
      );
    },
    carKnown() {
      const v = this.vehicle || {};
      if (v.selectionLink && String(v.selectionLink).trim()) return true;
      if (v.auto && String(v.auto).trim()) return true;
      return !!(v.carBrand && String(v.carBrand).trim());
    },
    carLabel() {
      const v = this.vehicle || {};
      if (v.auto && String(v.auto).trim()) return String(v.auto).trim();
      const parts = [v.carBrand, v.carModel, v.carYear].filter(Boolean);
      if (parts.length) return parts.join(' ');
      if (v.selectionLink) return 'Авто выбрано';
      return '—';
    },
  },
  watch: {
    initialCity: {
      immediate: true,
      handler(val) {
        if (val && !this.form.city) this.form.city = val;
      },
    },
    carKnown(val) {
      if (val && this.initialCity && !this.form.city) this.form.city = this.initialCity;
    },
    isModalOpen(open) {
      if (open) {
        this.lockBodyScroll();
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.syncFromOrder();
        this.loadCatalog();
      } else {
        this.unlockBodyScroll();
        document.removeEventListener('keydown', this.onDocumentKeydown);
        if (this.catalogReloadTimer) clearTimeout(this.catalogReloadTimer);
        this.catalogBoundsApplied = false;
        this.scriptText = '';
        this.scriptPhase = 0;
      }
    },
    vehicle: {
      deep: true,
      handler() {
        if (this.isModalOpen) this.loadCatalog();
      },
    },
    orderContext: {
      deep: true,
      handler() {
        if (this.isModalOpen) this.syncFromOrder();
      },
    },
    'form.tradeIn'() {
      if (this.isModalOpen) this.scheduleCatalogReload();
    },
    'form.agmEfb'() {
      if (this.isModalOpen) this.scheduleCatalogReload();
    },
    'form.fulfillment'() {
      if (this.isModalOpen) this.scheduleCatalogReload();
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.unlockBodyScroll();
  },
  created() {
    this.loadBotSettings();
  },
  methods: {
    onDocumentKeydown(e) {
      if (e.key === 'Escape' && this.isModalOpen) this.closeModal();
    },
    lockBodyScroll() {
      this.bodyOverflowBackup = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.overflow = this.bodyOverflowBackup || '';
    },
    setStyle(value) {
      this.botSettings.style = value;
      this.saveBotSettings();
    },
    setResponseSize(value) {
      this.botSettings.responseSize = value;
      this.saveBotSettings();
    },
    loadBotSettings() {
      try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.style) this.botSettings.style = parsed.style;
          if (parsed.responseSize) this.botSettings.responseSize = parsed.responseSize;
        }
      } catch (e) { /* ignore */ }
    },
    saveBotSettings() {
      try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.botSettings));
      } catch (e) { /* ignore */ }
    },
    openModal() {
      if (this.initialCity && !this.form.city) this.form.city = this.initialCity;
      this.isModalOpen = true;
      this.error = '';
      this.syncFromOrder();
    },
    syncFromOrder() {
      applyOrderContextToForm(this.orderContext, this.form);
      if (this.initialCity && !this.form.city) this.form.city = this.initialCity;
    },
    scheduleCatalogReload() {
      if (this.catalogReloadTimer) clearTimeout(this.catalogReloadTimer);
      this.catalogReloadTimer = setTimeout(() => this.loadCatalog(), 400);
    },
    buildQuestionnaire(opts) {
      const options = opts || {};
      if (options.includeCorpusBounds) {
        options.corpusBounds = this.catalogFullBounds;
      }
      return buildQuestionnairePayload(this.form, options);
    },
    async loadCatalog() {
      if (!this.carKnown) return;
      this.catalogLoading = true;
      try {
        const res = await fetch(CATALOG_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicle: this.buildVehiclePayload(),
            questionnaire: {
              ...this.buildQuestionnaire({ includePrice: false }),
              filter_catalog_by_price: false,
            },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Не удалось загрузить каталог');
        this.catalogItems = normalizeCatalogItems(data.catalog || data.catalog_preview || []);
        const bounds = calcCatalogPriceBounds(this.catalogItems);
        const sugMin = data.price_min_suggested != null ? data.price_min_suggested : bounds.min;
        const sugMax = data.price_max_suggested != null ? data.price_max_suggested : bounds.max;
        this.catalogFullBounds = { min: sugMin, max: sugMax };
        if (!this.catalogBoundsApplied) {
          this.applyingCatalogBounds = true;
          if (sugMin != null) this.form.priceMin = sugMin;
          if (sugMax != null) this.form.priceMax = sugMax;
          this.catalogBoundsApplied = true;
          this.$nextTick(() => { this.applyingCatalogBounds = false; });
        }
      } catch (e) {
        this.catalogItems = [];
        this.catalogFullBounds = { min: null, max: null };
      } finally {
        this.catalogLoading = false;
      }
    },
    closeModal() {
      this.isModalOpen = false;
    },
    scrollToScript() {
      this.$nextTick(() => {
        const el = this.$refs.scriptBlock;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    formatMinskShortAddress(address) {
      return String(address || '')
        .replace(/^г\.\s*минск,\s*ул\.\s*/i, '')
        .trim();
    },
    onFulfillmentChange(code) {
      this.form.fulfillment = code;
      if (!this.form.deliveryTypeLabel) {
        const labels = { pickup: 'Самовывоз', delivery: 'Доставка', unknown: 'Не уточняли' };
        this.form.deliveryTypeLabel = labels[code] || '';
      }
      if (this.isModalOpen) this.scheduleCatalogReload();
    },
    onDeliveryFromOrderChange(dt) {
      if (!dt) return;
      this.form.deliveryTypeCode = dt.value || '';
      this.form.deliveryTypeLabel = dt.label || dt.value || '';
      this.form.fulfillment = mapDeliveryToFulfillment(dt.value);
    },
    buildVehiclePayload() {
      const v = this.vehicle || {};
      return {
        carBrand: v.carBrand || '',
        carModel: v.carModel || '',
        carName: v.carName || '',
        carYear: v.carYear || '',
        fuelType: v.fuelType || '',
        carBodyType: v.carBodyType || '',
        auto: v.auto || '',
        selectionLink: v.selectionLink || '',
      };
    },
    async requestScriptIntro() {
      await this.requestScript('intro', 1);
    },
    async requestScriptRefined() {
      await this.requestScript('refined', 2);
    },
    async requestScript(dialogPhase, phaseNum) {
      if (!this.carKnown) {
        this.error = 'Сначала укажите автомобиль';
        return;
      }
      this.loadingScript = true;
      this.scriptPhaseRequest = phaseNum;
      this.error = '';
      this.saveBotSettings();

      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicle: this.buildVehiclePayload(),
            questionnaire: this.buildQuestionnaire({ includeCorpusBounds: true }),
            botSettings: {
              style: this.botSettings.style,
              responseSize: this.botSettings.responseSize,
            },
            dialog_phase: dialogPhase,
            catalog_snapshot: this.catalogItemsFiltered.slice(0, 12),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Не удалось сформировать сценарий');
        const text = data.text || '';
        if (!text) throw new Error('Пустой ответ от сервиса');
        this.scriptText = text;
        this.scriptPhase = phaseNum;
        this.lastScriptFormKey = this.formScriptKey;
        this.pickupStoresFromApi = data.pickup_stores || [];
        this.drive2Check = data.drive2_check && data.drive2_check.summary
          ? data.drive2_check
          : (data.drive2_check && data.drive2_check.status === 'not_found' ? data.drive2_check : null);
        if (data.price_min_corpus != null || data.price_max_corpus != null) {
          this.catalogFullBounds = {
            min: data.price_min_corpus != null ? data.price_min_corpus : this.catalogFullBounds.min,
            max: data.price_max_corpus != null ? data.price_max_corpus : this.catalogFullBounds.max,
          };
        }
        this.$emit('recommendation', {
          text,
          questionnaire: this.buildQuestionnaire(),
          catalog: this.catalogItemsFiltered.slice(0, 8),
          fullText: this.buildFullRecommendationText(),
        });
        this.scrollToScript();
      } catch (e) {
        this.error = e.message || 'Ошибка запроса к ИИ';
      } finally {
        this.loadingScript = false;
        this.scriptPhaseRequest = 0;
      }
    },
    buildFullRecommendationText() {
      if (!this.scriptText) return '';
      const stamp = new Date().toLocaleString('ru-RU');
      const phaseLabel = this.scriptPhase === 2 ? 'ответ 2' : 'ответ 1';
      return [`[ИИ подбор ${stamp}, ${phaseLabel}]`, this.scriptText].join('\n');
    },
  },
};
</script>

<style scoped>
/* —— Кнопка в форме заказа —— */
.battery-ai-block { margin: 12px 0 16px; }
.battery-ai-block_embedded { margin: 0; position: absolute; top: 6px; right: 6px; z-index: 2; }
.battery-ai-open-btn_embedded { padding: 5px 10px; font-size: 11px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0, 94, 235, 0.25); }
.battery-ai-car-done { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; }
.battery-ai-car-done-icon { width: 32px; height: 32px; border-radius: 50%; background: #005eeb; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; flex-shrink: 0; }
.battery-ai-car-done-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.battery-ai-car-done-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #64748b; }
.battery-ai-car-done-value { font-size: 14px; font-weight: 600; color: #0f172a; }
.battery-ai-open-btn { border: none; border-radius: 8px; padding: 10px 18px; background: #005eeb; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; }
.battery-ai-open-btn:hover { background: #0049c2; }

/* —— Анимация —— */
.bai-fade-enter-active, .bai-fade-leave-active { transition: opacity 0.18s ease; }
.bai-fade-enter, .bai-fade-leave-to { opacity: 0; }

/* —— Полноэкранная модалка (изолированные стили) —— */
.bai {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  background: #eef1f6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #1e293b;
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}

.bai *,
.bai *::before,
.bai *::after {
  box-sizing: border-box;
}

.bai__app {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
}

/* Шапка */
.bai__topbar {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 20px;
  padding: 16px 28px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
}

.bai__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.bai__logo {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, #2563eb, #005eeb);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 94, 235, 0.35);
}

.bai__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.bai__subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #64748b;
}

.bai__vehicle {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 480px;
  margin: 0 auto;
  padding: 10px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.bai__vehicle-icon { flex-shrink: 0; color: #005eeb; }
.bai__vehicle-text {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bai__topbar-end {
  display: flex;
  align-items: center;
  gap: 16px;
}

.bai__settings {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.bai__setting-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bai__setting-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
}

.bai__segments {
  display: inline-flex;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  gap: 2px;
}

.bai__segment {
  border: none;
  background: transparent;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
  font-family: inherit;
}

.bai__segment:hover { color: #334155; background: rgba(255, 255, 255, 0.7); }
.bai__segment--on { background: #fff; color: #005eeb; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }

.bai__close {
  width: 44px;
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  flex-shrink: 0;
  font-family: inherit;
}

.bai__close:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

/* Контент — анкета + сценарий */
.bai__content {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(340px, 420px) 1fr;
  gap: 20px;
  padding: 20px 28px;
  overflow: hidden;
}

.bai__panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.bai__panel-head {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f1f5f9;
}

.bai__panel-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.bai__step {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #005eeb;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bai__panel-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
  line-height: 1.4;
}

/* Анкета */
.bai__panel--form .bai__form {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.bai__fieldset {
  margin: 0 0 20px;
  padding: 0;
  border: none;
}

.bai__fieldset legend {
  padding: 0;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.bai__field {
  margin-bottom: 20px;
}

.bai__field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.bai__input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 15px;
  color: #0f172a;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.12s, box-shadow 0.12s;
}

.bai__input:focus {
  outline: none;
  border-color: #005eeb;
  box-shadow: 0 0 0 3px rgba(0, 94, 235, 0.15);
}

.bai__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bai__chip {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: all 0.12s;
  user-select: none;
}

.bai__chip input { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
.bai__chip:hover { border-color: #93c5fd; background: #f8fafc; }
.bai__chip--on { border-color: #005eeb; background: #eff6ff; color: #005eeb; font-weight: 600; }

.bai__budget {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.bai__budget-btn {
  height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
}

.bai__budget-btn:hover { border-color: #93c5fd; }
.bai__budget-btn--on { border-color: #005eeb; background: #eff6ff; color: #005eeb; }

.bai__sync-box {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
}

.bai__sync-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #005eeb;
  margin-bottom: 8px;
}

.bai__sync-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #475569;
}

.bai__sync-row + .bai__sync-row { margin-top: 6px; }

.bai__sync-row strong { color: #0f172a; font-weight: 700; }

.bai__field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.35;
}

.bai__delivery-badge {
  display: inline-block;
  padding: 8px 14px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.bai__chips--mt { margin-top: 10px; }

.bai__chip--btn {
  border: none;
  font-family: inherit;
}

.bai__check {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}

.bai__check input {
  width: 18px;
  height: 18px;
  accent-color: #005eeb;
}

.bai__fieldset--catalog {
  margin-bottom: 16px;
  min-width: 0;
}

.bai__legend-sub {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #64748b;
}

.bai__price-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.bai__price-field label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.bai__input--num { height: 44px; }

.bai__error {
  margin: 0 20px 16px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #b91c1c;
  font-size: 13px;
}

/* Сценарий для клиента */
.bai__panel--script {
  min-width: 0;
  min-height: 0;
}

.bai__panel--script-ready {
  border-color: #bfdbfe;
  box-shadow: 0 4px 20px rgba(0, 94, 235, 0.08);
}

.bai__script-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bai__script-upper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 42vh;
  overflow-y: auto;
}

.bai__script-stale {
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #c2410c;
}

.bai__script-phase {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #005eeb;
}

.bai__script-text {
  flex-shrink: 0;
  max-height: 38vh;
  min-height: 120px;
  padding: 20px 22px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  font-size: 17px;
  line-height: 1.65;
  color: #0f172a;
  white-space: pre-wrap;
  overflow-y: auto;
}

.bai__drive2 {
  flex-shrink: 0;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}
.bai__drive2-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #475569;
  margin-bottom: 4px;
}
.bai__drive2-summary {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #334155;
}
.bai__drive2-meta {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
}
.bai__drive2-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}
.bai__drive2-links a {
  font-size: 12px;
  color: #005eeb;
  text-decoration: none;
}
.bai__drive2-links a:hover { text-decoration: underline; }

.bai__script-catalog {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}

.bai__catalog-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.bai__catalog-count {
  padding: 2px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #005eeb;
  font-size: 10px;
}

.bai__catalog-filter {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: #94a3b8;
}

.bai__script-catalog >>> .bct--fill {
  flex: 1;
  min-height: 0;
}

.bai__minsk-stores {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  line-height: 1.3;
  color: #334155;
  min-width: 0;
}

.bai__minsk-label {
  flex-shrink: 0;
  font-weight: 700;
  color: #005eeb;
  white-space: nowrap;
}

.bai__minsk-line {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bai__script-hint {
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
}

.bai__script-hint--loading {
  color: #005eeb;
  border-color: #bfdbfe;
  background: #eff6ff;
}

.bai__state--compact {
  min-height: 100px;
  padding: 16px 12px;
}

.bai__state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 12px;
  min-height: 180px;
}

.bai__state-icon,
.bai__loader {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  margin-bottom: 16px;
}

.bai__state-icon {
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  color: #005eeb;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bai__loader {
  border: 3px solid #e2e8f0;
  border-top-color: #005eeb;
  border-radius: 50%;
  animation: bai-spin 0.7s linear infinite;
}

@keyframes bai-spin { to { transform: rotate(360deg); } }

.bai__state-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.bai__state-sub {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  max-width: 280px;
  line-height: 1.45;
}


/* Подвал */
.bai__footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 28px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.bai__footer-tip {
  font-size: 12px;
  color: #94a3b8;
}

.bai__footer-tip kbd {
  display: inline-block;
  padding: 2px 7px;
  margin-right: 4px;
  border-radius: 5px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 11px;
  font-family: inherit;
  color: #64748b;
}

.bai__footer-btns { display: flex; gap: 10px; flex-shrink: 0; }

.bai__btn {
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.bai__btn--ghost {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
}

.bai__btn--ghost:hover { background: #f8fafc; border-color: #94a3b8; }

.bai__btn--primary {
  min-width: 260px;
  border: none;
  background: #005eeb;
  color: #fff;
  box-shadow: 0 4px 14px rgba(0, 94, 235, 0.35);
}

.bai__btn--primary:hover:not(:disabled) { background: #0049c2; }
.bai__btn--primary:disabled { opacity: 0.65; cursor: wait; }

.bai__btn--secondary {
  min-width: 200px;
  border: 1px solid #005eeb;
  background: #fff;
  color: #005eeb;
}

.bai__btn--secondary:hover:not(:disabled) { background: #eff6ff; }
.bai__btn--secondary:disabled { opacity: 0.5; cursor: not-allowed; }

.bai__btn-loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bai-spin 0.6s linear infinite;
}

.bai__btn-loader--dark {
  border-color: rgba(0, 94, 235, 0.25);
  border-top-color: #005eeb;
}

/* Скроллбары */
.bai__panel--form .bai__form::-webkit-scrollbar,
.bai__script-body::-webkit-scrollbar,
.bai__script-text::-webkit-scrollbar {
  width: 6px;
}

.bai__panel--form .bai__form::-webkit-scrollbar-thumb,
.bai__script-body::-webkit-scrollbar-thumb,
.bai__script-text::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

/* Адаптив */
@media (max-width: 1100px) {
  .bai__content {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .bai__panel--script {
    min-height: 320px;
  }
}

@media (max-width: 900px) {
  .bai__topbar {
    grid-template-columns: 1fr auto;
    gap: 12px;
  }

  .bai__vehicle { display: none; }

  .bai__settings { display: none; }

  .bai__content {
    grid-template-columns: 1fr;
    overflow-y: auto;
    padding: 16px;
  }

  .bai__panel { max-height: none; }
  .bai__panel--result { max-height: none; }

  .bai__footer {
    flex-direction: column;
    align-items: stretch;
    padding: 12px 16px;
  }

  .bai__footer-btns { flex-direction: column-reverse; }
  .bai__btn--primary { min-width: 0; width: 100%; }
}
</style>

<style>
.bai { z-index: 2147483000 !important; }
/* Сброс глобальных стилей CRM внутри модалки */
.bai button,
.bai input,
.bai select,
.bai textarea {
  font-family: inherit;
  line-height: inherit;
}
.bai fieldset { min-width: 0; }
.bai h2, .bai h3, .bai h4, .bai p, .bai ul, .bai li { margin: 0; padding: 0; }
.bai ul { list-style: disc; }
.bai label { font-weight: inherit; }
</style>
